import React, { useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { cn } from "../../utils/helpers";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  emptyLabel: string;
  searchPlaceholder: string;
  itemLabel?: (option: MultiSelectOption) => string;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  options,
  selected,
  onChange,
  emptyLabel,
  searchPlaceholder,
  itemLabel = (o) => o.label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = options.filter((o) =>
    itemLabel(o).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => selected.includes(o.value));

  const handleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const drop = new Set(filtered.map((o) => o.value));
      onChange(selected.filter((v) => !drop.has(v)));
    } else {
      const add = filtered
        .map((o) => o.value)
        .filter((v) => !selected.includes(v));
      onChange([...selected, ...add]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors min-w-[200px]"
      >
        <span className="text-sm text-left truncate">
          {selected.length === 0 ? emptyLabel : `${selected.length} selected`}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
            aria-hidden
          />

          <div className="absolute top-full left-0 mt-1 w-full min-w-[240px] max-w-md bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="p-3 border-b border-gray-200">
              <button
                type="button"
                onClick={handleSelectAllFiltered}
                disabled={filtered.length === 0}
                className="flex items-center space-x-3 w-full p-2 hover:bg-gray-100 rounded text-sm transition-colors disabled:opacity-50"
              >
                <div
                  className={cn(
                    "w-4 h-4 border-2 rounded flex items-center justify-center transition-all shrink-0",
                    allFilteredSelected && filtered.length > 0
                      ? "bg-blue-600 border-blue-600"
                      : "border-gray-300 bg-white",
                  )}
                >
                  {allFilteredSelected && filtered.length > 0 && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="font-medium">
                  Select all shown ({filtered.length})
                </span>
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {searchTerm ? "No matches" : "No options"}
                </div>
              ) : (
                filtered.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => toggle(option.value)}
                      className="flex items-center space-x-3 w-full p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-left"
                    >
                      <div
                        className={cn(
                          "w-4 h-4 border-2 rounded flex items-center justify-center transition-all shrink-0",
                          isSelected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300 bg-white hover:border-blue-400",
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-gray-900 truncate">
                        {itemLabel(option)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
